import { ChapterAudioDetail } from 'src/interfaces'

const normalizeStatus = (status?: string | null) =>
	String(status ?? '').toLowerCase().trim()

export const isDocProcessingStatus = (status?: string | null) =>
	normalizeStatus(status) === 'pending_text'

export const isAudioProcessingStatus = (status?: string | null) => {
	const normalized = normalizeStatus(status)
	return normalized === 'pending_text' || normalized === 'pending_audio'
}

export const isDocFailedStatus = (status?: string | null) =>
	normalizeStatus(status) === 'failed_text'

export const isAudioFailedStatus = (status?: string | null) =>
	normalizeStatus(status) === 'failed_audio'

export const resolveLanguageDocState = (
	details: ChapterAudioDetail[],
	fallbackDocUrl?: string | null,
) => {
	const docProcessing = details.some((item) => isDocProcessingStatus(item.status))
	const docUrl =
		details.find(
			(item) => item.doc_url && !isDocFailedStatus(item.status),
		)?.doc_url ??
		(!details.some((item) => isDocFailedStatus(item.status))
			? fallbackDocUrl ?? null
			: null)

	return { docUrl, docProcessing }
}

export const shouldShowAudioForDetail = (detail: ChapterAudioDetail) => {
	if (isAudioFailedStatus(detail.status)) return false
	if (detail.url) return true
	return isAudioProcessingStatus(detail.status)
}

export const languageSortOrder = (language: string) => {
	if (language === 'en') return 0
	if (language === 'vi') return 1
	return 2
}

export type HighlightStatus = 'none' | 'available' | 'incorrect'

export const HIGHLIGHT_STATUS_CONFIG: Record<
	HighlightStatus,
	{ label: string; color: string }
> = {
	none: { label: 'No highlight.', color: '#9ca3af' },
	available: { label: 'Highlight is available!', color: '#1f7a44' },
	incorrect: { label: 'Highlight is not correct!', color: '#dc2626' },
}

const hasNonEmptyList = (value: unknown): boolean =>
	Array.isArray(value) && value.length > 0

const getAlignmentField = (
	record: Record<string, unknown>,
	...keys: string[]
): unknown => {
	for (const key of keys) {
		if (key in record) return record[key]
	}
	return undefined
}

const getAlignmentCharacters = (record: Record<string, unknown>) =>
	getAlignmentField(record, 'characters', 'chars')

const getAlignmentStartTimes = (record: Record<string, unknown>) =>
	getAlignmentField(
		record,
		'character_start_times_seconds',
		'characterStartTimesSeconds',
	)

const getAlignmentEndTimes = (record: Record<string, unknown>) =>
	getAlignmentField(
		record,
		'character_end_times_seconds',
		'characterEndTimesSeconds',
	)

const hasAnyAlignmentData = (record: Record<string, unknown>) => {
	if (hasNonEmptyList(getAlignmentCharacters(record))) return true
	if (hasNonEmptyList(getAlignmentStartTimes(record))) return true
	if (hasNonEmptyList(getAlignmentEndTimes(record))) return true

	return Object.values(record).some((value) => {
		if (Array.isArray(value)) return value.length > 0
		return value != null && value !== ''
	})
}

const getAlignmentRecord = (alignment: unknown): Record<string, unknown> | null => {
	if (alignment == null) return null

	if (typeof alignment === 'string') {
		const trimmed = alignment.trim()
		if (!trimmed) return null
		try {
			const parsed = JSON.parse(trimmed)
			if (typeof parsed === 'object' && parsed !== null && !Array.isArray(parsed)) {
				return parsed as Record<string, unknown>
			}
		} catch {
			return null
		}
		return null
	}

	if (typeof alignment === 'object' && !Array.isArray(alignment)) {
		return alignment as Record<string, unknown>
	}

	return null
}

export const resolveHighlightStatus = (alignment: unknown): HighlightStatus => {
	const record = getAlignmentRecord(alignment)
	if (!record) return 'none'

	const hasStartTimes = hasNonEmptyList(getAlignmentStartTimes(record))
	const hasEndTimes = hasNonEmptyList(getAlignmentEndTimes(record))

	if (hasStartTimes && hasEndTimes) return 'available'
	if (hasAnyAlignmentData(record)) return 'incorrect'

	return 'none'
}
